<template>
  <div class="loan-configuration-container">
    <form @submit.prevent="saveLoanConfiguration">
      <div>
        <div class="form-group tw-mt-6">
          <label
            class="tw-text-[#323E67] tw-leading-[-150%] tw-tracking-[-0.12px] tw-text-sm tw-mb-2 tw-block"
          >
            Investment Categories
          </label>
          <p class="tw-text-sm tw-text-[#8791A7] tw-mb-2">
            Select investment categories you provide collateral credit for
          </p>
          <multiselect
            v-model="loanConfig.investmentCategories"
            :options="investmentCategories"
            :multiple="true"
            :clear-on-select="false"
            :preserve-search="true"
            placeholder="Select categories"
            :close-on-select="true"
          >
            <template #caret>
              <img
                src="https://res.cloudinary.com/djalafcj9/image/upload/v1653918409/getequityV2/Frame_jhtrqy.svg"
                class="tw-absolute tw-right-2 sm:tw-right-4 tw-top-3"
                alt="chevron icon"
              />
            </template>

            <template v-slot:tag="{ option }">
              <div
                class="tw-flex tw-items-center tw-bg-[#F0F5FF] tw-text-blue-darker tw-rounded-full tw-py-1 tw-px-3 tw-gap-2 tw-mb-2 tw-mr-2"
              >
                <span class="tw-text-blue-600 tw-font-medium">{{
                  option
                }}</span>
              </div>
            </template>
          </multiselect>
        </div>

        <div class="form-group tw-mt-6">
          <label
            class="tw-text-[#323E67] tw-leading-[-150%] tw-tracking-[-0.12px] tw-text-sm tw-mb-2 tw-block"
          >
            Supported Currencies
          </label>
          <!-- <p class="tw-text-sm tw-text-[#8791A7] tw-mb-2">
              You can select multiple currencies for your credit terms
            </p> -->
          <multiselect
            v-model="loanConfig.supportedCurrencies"
            :options="currencyOptions"
            :multiple="true"
            :clear-on-select="false"
            :preserve-search="true"
            placeholder="Select currencies"
            :close-on-select="true"
            label="currency"
            track-by="currency"
          >
            <template #caret>
              <img
                src="https://res.cloudinary.com/djalafcj9/image/upload/v1653918409/getequityV2/Frame_jhtrqy.svg"
                class="tw-absolute tw-right-2 sm:tw-right-4 tw-top-3"
                alt="chevron icon"
              />
            </template>

            <template v-slot:option="{ option }">
              <div class="tw-flex tw-items-center tw-gap-2">
                <img
                  :src="option.icon"
                  class="tw-w-5 tw-h-3.5 tw-rounded-full"
                />
                <span class="tw-font-medium">{{ option.currency }}</span>
              </div>
            </template>

            <template v-slot:tag="{ option }">
              <div
                class="tw-flex tw-items-center tw-bg-[#F0F5FF] tw-text-blue-darker tw-rounded-full tw-py-1 tw-px-3 tw-gap-2 tw-mb-2 tw-mr-2"
              >
                <img
                  :src="option.icon"
                  class="tw-w-5 tw-h-3.5 tw-rounded-full"
                />
                <span class="tw-text-blue-600 tw-font-medium">{{
                  option.currency
                }}</span>
              </div>
            </template>
          </multiselect>
          <p class="tw-text-sm tw-text-[#8791A7] tw-mt-2">
            You can select multiple currencies for your credit terms.
          </p>
        </div>
      </div>

      <div class="overlay-container">
        <div class="overlay" v-if="loanConfig.useApiDefinedTerms"></div>

        <div
          v-if="
            loanConfig.supportedCurrencies &&
            loanConfig.supportedCurrencies.length > 0
          "
          class="tw-mt-6"
        >
          <div id="table-header" class="tw-relative">
            <div class="sm:tw-hidden">
              <label for="currency-tabs">Select Currency</label>
              <select
                class="tw-w-full tw-bg-gray-light tw-text-sm tw-rounded tw-p-4 tw-cursor-pointer tw-border-0 focus:tw-border-primary"
                name="currency-tabs"
                id="currency-tabs"
                v-model="selectedCurrency"
              >
                <option
                  class="tw-flex tw-items-center"
                  v-for="currency in loanConfig.supportedCurrencies"
                  :value="currency"
                  :key="currency.currency"
                >
                  {{ currency.currency }}
                </option>
              </select>
              <img
                src="@/assets/img/chevron-down.svg"
                class="tw-absolute tw-right-2 tw-bottom-6"
                alt="chevron icon"
              />
            </div>

            <div class="tw-hidden sm:tw-block">
              <ul
                class="tw-flex tw-border-b tw-border-gray_dark tw-overflow-auto"
              >
                <li
                  class="tw-font-CircularStd-medium tw-font-medium tw-cursor-pointer tw-transition-all tw-duration-300 hover:tw-border-primary tw-py-4 tw-px-6"
                  :class="[
                    selectedCurrency &&
                    selectedCurrency.currency === currency.currency
                      ? 'tw-border-b-4 tw-border-primary'
                      : 'tw-text-gray',
                  ]"
                  v-for="currency in loanConfig.supportedCurrencies"
                  :key="currency.currency"
                  @click="selectedCurrency = currency"
                >
                  <div class="tw-flex tw-items-center tw-gap-2">
                    <img :src="currency.icon" class="tw-w-5 tw-h-3.5" />
                    {{ currency.currency }}
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <div v-if="selectedCurrency" class="tw-mt-6">
            <div
              class="tw-border tw-border-gray-200 tw-p-4 tw-rounded-md tw-mb-6"
            >
              <h3 class="tw-text-lg tw-font-medium tw-mb-4">
                {{ selectedCurrency.currency }} Configuration
              </h3>

              <div class="form-grid">
                <div class="form-group">
                  <label :for="`maxLoanAmount-${selectedCurrency.currency}`">
                    Max Loan Amount ({{ selectedCurrency.currency }})
                  </label>
                  <p>
                    What is the maximum loan you can provide for an individual
                  </p>
                  <input
                    :id="`maxLoanAmount-${selectedCurrency.currency}`"
                    v-model.number="currentCurrencyConfig.maxLoanAmount"
                    type="number"
                    min="0"
                    :required="!loanConfig.useApiDefinedTerms"
                    placeholder="Maximum Loan Amount"
                  />
                </div>

                <div class="form-group">
                  <label
                    :for="`monthlyInterestRate-${selectedCurrency.currency}`"
                  >
                    Monthly Interest Rate
                  </label>
                  <p>What is your monthly interest rate?</p>
                  <input
                    :id="`monthlyInterestRate-${selectedCurrency.currency}`"
                    v-model.number="currentCurrencyConfig.monthlyInterestRate"
                    type="number"
                    step="0.01"
                    min="0"
                    :required="!loanConfig.useApiDefinedTerms"
                    placeholder="Monthly Interest Rate"
                  />
                </div>

                <div class="form-group">
                  <label :for="`maxLoanDuration-${selectedCurrency.currency}`">
                    Max Loan Duration
                  </label>
                  <p>
                    What is the maximum duration in months you can provide a
                    loan for?
                  </p>
                  <input
                    :id="`maxLoanDuration-${selectedCurrency.currency}`"
                    v-model.number="currentCurrencyConfig.maxLoanDuration"
                    type="number"
                    min="0"
                    :required="!loanConfig.useApiDefinedTerms"
                    placeholder="Maximum Loan Duration"
                  />
                </div>

                <div class="form-group">
                  <label
                    :for="`loanCollateralRatio-${selectedCurrency.currency}`"
                  >
                    Loan Collateral Ratio
                  </label>
                  <p>
                    What percentage of cash against the value of the
                    collaterized asset
                  </p>
                  <input
                    :id="`loanCollateralRatio-${selectedCurrency.currency}`"
                    v-model.number="currentCurrencyConfig.loanCollateralRatio"
                    type="number"
                    step="0.01"
                    min="0"
                    :required="!loanConfig.useApiDefinedTerms"
                    placeholder="Loan Collateral Ratio"
                  />
                </div>

                <div class="form-group">
                  <label :for="`gracePeriod-${selectedCurrency.currency}`">
                    Grace Period
                  </label>
                  <p>Provide Grace Period</p>
                  <input
                    :id="`gracePeriod-${selectedCurrency.currency}`"
                    v-model.number="currentCurrencyConfig.gracePeriod"
                    type="number"
                    min="0"
                    :required="!loanConfig.useApiDefinedTerms"
                    placeholder="Grace Period"
                  />
                </div>

                <div class="form-group">
                  <label
                    :for="`gracePeriodUnit-${selectedCurrency.currency}`"
                    class="tw-invisible"
                  >
                    Grace Period Unit
                  </label>

                  <div></div>

                  <Dropdown
                    class="tw-mb-4"
                    :name="`gracePeriodUnit-${selectedCurrency.currency}`"
                    label="Select Grace Period Unit"
                    :validate="validateGracePeriodUnit"
                    :currentValue="currentCurrencyConfig.gracePeriodUnit"
                    :options="gracePeriodOptions"
                    @input-changed="
                      (value) =>
                        handleGracePeriodUnitChange(
                          value,
                          selectedCurrency.currency
                        )
                    "
                    @is-valid="handleGracePeriodValidation"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="checkbox-group tw-mt-6">
        <input
          id="useApiDefinedTerms"
          v-model="loanConfig.useApiDefinedTerms"
          type="checkbox"
          class="custom-checkbox"
        />
        <label for="useApiDefinedTerms">
          <span class="checkmark"></span>
          Use API Defined Terms
        </label>
      </div>

      <div class="form-group tw-mt-4" v-if="loanConfig.useApiDefinedTerms">
        <label for="provider_url">Add your URL</label>
        <input
          id="provider_url"
          v-model="loanConfig.provider_url"
          required
          placeholder="Provider url"
          class="tw-w-fit"
        />
      </div>

      <div class="button-container">
        <button type="submit">
          {{ isConfigExisting ? "Update" : "Save" }} Loan Configuration
          <svg
            width="6"
            height="10"
            viewBox="0 0 6 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3.17202 4.99984L0.343018 2.17184L1.75702 0.756836L6.00002 4.99984L1.75702 9.24284L0.343018 7.82784L3.17202 4.99984Z"
              fill="white"
            />
          </svg>
        </button>
      </div>
    </form>
  </div>
</template>
  
<script>
import { set } from "vue-demi";
import { mapGetters, mapActions } from "vuex";
import Dropdown from "@/components/creditProvider/Dropdown";
import Multiselect from "vue-multiselect";
import US from "@/assets/img/flags/usa-flag.svg";
import Kenya from "@/assets/img/flags/kenyan-flag.svg";
import Nigeria from "@/assets/img/flags/nigerian-flag.svg";

export default {
  name: "LoanTermConfiguration",
  components: {
    Dropdown,
    Multiselect,
  },
  data() {
    return {
      loanConfig: {
        useApiDefinedTerms: false,
        provider_url: null,
        investmentCategories: [],
        supportedCurrencies: [],
      },
      currencyConfigs: {},
      formValidation: {
        gracePeriodUnit: true,
      },
      gracePeriodOptions: [
        { value: "DAYS", label: "Days" },
        { value: "MONTHS", label: "Months" },
      ],
      currencyOptions: [
        {
          currency: "NGN",
          icon: Nigeria,
        },
        {
          currency: "USD",
          icon: US,
        },
        {
          currency: "KES",
          icon: Kenya,
        },
      ],
      isConfigExisting: false,
      selectedCurrency: null,
    };
  },
  computed: {
    ...mapGetters("organisationModule", [
      "loanConfiguration",
      "organisationDetails",
      "investmentCategories",
    ]),
    isFormValid() {
      return Object.values(this.formValidation).every(
        (value) => value === true
      );
    },
    // current currency specific data for v-bind
    currentCurrencyConfig() {
      if (!this.selectedCurrency) return {};

      const code = this.selectedCurrency.currency;

      if (!this.currencyConfigs[code]) {
        set(this.currencyConfigs, code, {
          maxLoanAmount: 0,
          monthlyInterestRate: 0,
          maxLoanDuration: 0,
          loanCollateralRatio: 0,
          gracePeriod: 0,
          gracePeriodUnit: "DAYS",
        });
      }

      return this.currencyConfigs[code];
    },
  },
  created() {
    this.fetchLoanConfiguration();
    this.fetchInvestmentCategories();
  },
  watch: {
    // Watch data gotten to set default values
    loanConfiguration: {
      handler(newVal) {
        this.isConfigExisting = newVal && Object.keys(newVal).length > 0;

        if (this.isConfigExisting) {
          this.mapResponseToFormData(newVal);
        }
      },
      immediate: true,
      deep: true,
    },
    // Watch to set currency specific data
    "loanConfig.supportedCurrencies": {
      handler(newCurrencies) {
        if (Array.isArray(newCurrencies)) {
          newCurrencies.forEach((currency) => {
            if (!this.currencyConfigs[currency]) {
              set(this.currencyConfigs, currency, {
                maxLoanAmount: 0,
                monthlyInterestRate: 0,
                maxLoanDuration: 0,
                loanCollateralRatio: 0,
                gracePeriod: 0,
                gracePeriodUnit: "DAYS",
              });
            }
          });

          if (
            !this.selectedCurrency ||
            !newCurrencies.includes(this.selectedCurrency)
          ) {
            this.selectedCurrency = newCurrencies[0] || null;
          }
        } else {
          this.selectedCurrency = null;
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    ...mapActions("organisationModule", [
      "getLoanConfiguration",
      "updateLoanConfiguration",
      "createLoanConfiguration",
      "getInvestmentCategories",
    ]),
    ...mapActions(["showToast"]),

    getFullConfiguration() {
      return {
        general: this.loanConfig,
        currencySpecific: this.currencyConfigs,
      };
    },

    getCurrencyConfig(currencyCode) {
      if (this.currencyConfigs[currencyCode]) {
        return this.currencyConfigs[currencyCode];
      } else {
        set(this.currencyConfigs, currencyCode, {
          maxLoanAmount: 0,
          monthlyInterestRate: 0,
          maxLoanDuration: 0,
          loanCollateralRatio: 0,
          gracePeriod: 0,
          gracePeriodUnit: "DAYS",
        });
        return this.currencyConfigs[currencyCode];
      }
    },

    handleGracePeriodUnitChange(value, currencyCode) {
      if (!this.currencyConfigs[currencyCode]) {
        this.getCurrencyConfig(currencyCode);
      }
      this.currencyConfigs[currencyCode].gracePeriodUnit = value;
    },
    async fetchLoanConfiguration() {
      try {
        const res = await this.getLoanConfiguration();
        return res;
      } catch (error) {
        console.error("Error fetching loan configuration:", error);
      }
    },

    async fetchInvestmentCategories() {
      if (
        !this.investmentCategories ||
        this.investmentCategories.length === 0
      ) {
        try {
          await this.getInvestmentCategories();
        } catch (error) {
          this.showToast({
            description: "Failed to load investment categories",
            display: true,
            type: "error",
          });
        }
      }
    },

    // Helper to set default form values for general data and currency specific
    mapResponseToFormData(response) {
      if (!response || !Array.isArray(response) || response.length === 0)
        return;

      const firstConfig = response[0];

      this.loanConfig = {
        useApiDefinedTerms: firstConfig.use_api === true,
        provider_url: firstConfig.provider_url || null,
        investmentCategories: firstConfig.investment_categories || [],
        supportedCurrencies: [],
      };

      this.currencyConfigs = {};

      const foundCurrencyCodes = new Set();

      response.forEach((config) => {
        const currency = config.currency;

        if (currency) {
          foundCurrencyCodes.add(currency);

          this.currencyConfigs[currency] = {
            maxLoanAmount:
              config.max_amount !== undefined ? config.max_amount : 0,
            monthlyInterestRate:
              config.interest_rate !== undefined
                ? config.interest_rate * 100
                : 0,
            maxLoanDuration: config.duration || 0,
            loanCollateralRatio:
              config.collateral_ratio !== undefined
                ? config.collateral_ratio * 100
                : 0,
            gracePeriod:
              config.grace_period !== undefined ? config.grace_period : 0,
            gracePeriodUnit: config.grace_period_unit || "DAYS",
          };
        }
      });

      this.loanConfig.supportedCurrencies = this.currencyOptions.filter(
        (option) => foundCurrencyCodes.has(option.currency)
      );

      if (
        !this.selectedCurrency &&
        this.loanConfig.supportedCurrencies.length > 0
      ) {
        this.selectedCurrency = this.loanConfig.supportedCurrencies[0];
      }
    },

    // parallel request based on currency selected (added)
    async saveLoanConfiguration() {
      if (!this.validateForm()) return;

      const fullConfig = this.getFullConfiguration();
      const currencies =
        fullConfig.general.supportedCurrencies.map((curr) => curr.currency) ||
        [];

      // Helper to get config values for each currency
      const getConfigValue = (currency, propertyName, applyDivide = false) => {
        const currencySpecific = fullConfig.currencySpecific || {};
        const currencyConfig = currencySpecific[currency] || {};

        if (
          currencyConfig[propertyName] !== undefined &&
          currencyConfig[propertyName] !== null
        ) {
          return applyDivide
            ? currencyConfig[propertyName] / 100
            : currencyConfig[propertyName];
        }

        if (
          currency === "NGN" &&
          currencySpecific[propertyName] !== undefined &&
          currencySpecific[propertyName] !== null
        ) {
          return applyDivide
            ? currencySpecific[propertyName] / 100
            : currencySpecific[propertyName];
        }

        const generalValue = fullConfig.general[propertyName];
        if (generalValue !== undefined && generalValue !== null) {
          return applyDivide ? generalValue / 100 : generalValue;
        }

        const defaults = {
          maxLoanAmount: 0,
          maxLoanDuration: 0,
          monthlyInterestRate: 0,
          loanCollateralRatio: 0,
          gracePeriod: 0,
          gracePeriodUnit: "DAYS",
        };

        return applyDivide
          ? defaults[propertyName] / 100
          : defaults[propertyName];
      };

      const useApi = Boolean(fullConfig.general.useApiDefinedTerms);

      // Build base general settings
      const generalSettings = {
        investment_categories: fullConfig.general.investmentCategories || [],
        use_api: useApi,
      };

      if (useApi && fullConfig.general.provider_url) {
        generalSettings.provider_url = fullConfig.general.provider_url;
      }

      const configPromises = [];

      for (const currency of currencies) {
        let currencyConfig;

        if (useApi) {
          currencyConfig = {
            investment_categories: generalSettings.investment_categories,
            use_api: generalSettings.use_api,
            currency: currency,
          };

          // Only add provider_url if it exists
          if (generalSettings.provider_url) {
            currencyConfig.provider_url = generalSettings.provider_url;
          }
        } else {
          currencyConfig = {
            ...generalSettings,
            max_amount: getConfigValue(currency, "maxLoanAmount"),
            currency: currency,
            duration: getConfigValue(currency, "maxLoanDuration"),
            interest_rate: getConfigValue(
              currency,
              "monthlyInterestRate",
              true
            ),
            collateral_ratio: getConfigValue(
              currency,
              "loanCollateralRatio",
              true
            ),
            grace_period: getConfigValue(currency, "gracePeriod"),
            grace_period_unit: getConfigValue(currency, "gracePeriodUnit"),
          };
        }

        // Add creditor if config doesn't exist
        if (!this.isConfigExisting) {
          currencyConfig.creditor = this.organisationDetails._id;
        }

        configPromises.push(this.updateLoanConfiguration(currencyConfig));
      }

      try {
        await Promise.all(configPromises);
        this.showToast({
          description: `Loan configurations updated successfully`,
          display: true,
          type: "success",
        });
      } catch (error) {
        console.error("Error saving loan configurations:", error);
        this.showToast({
          description: error.message || "Failed to save loan configurations",
          display: true,
          type: "error",
        });
      }
    },

    handleGracePeriodValidation(isValid) {
      this.formValidation.gracePeriodUnit = isValid;
    },

    validateGracePeriodUnit(value) {
      if (!value) {
        return "Please select a grace period unit";
      }
      return true;
    },

    validateForm() {
      const { investmentCategories, supportedCurrencies, useApiDefinedTerms } =
        this.loanConfig;

      if (useApiDefinedTerms) {
        return true;
      }

      if (!investmentCategories || investmentCategories.length === 0) {
        this.showToast({
          description: "Please select at least one investment category",
          display: true,
          type: "error",
        });
        return false;
      }

      if (!supportedCurrencies || supportedCurrencies.length === 0) {
        this.showToast({
          description: "Please select at least one supported currency",
          display: true,
          type: "error",
        });
        return false;
      }

      for (const currency of supportedCurrencies) {
        const isValid = this.validateCurrencyConfig(currency.currency);
        if (!isValid) {
          return false;
        }
      }
      return true;
    },

    // Validate function helper for currency specific inputs
    validateCurrencyConfig(currency) {
      const config = this.getCurrencyConfig(currency);

      if (!config.maxLoanAmount || config.maxLoanAmount <= 0) {
        this.showToast({
          description: `${currency}: Max loan amount must be greater than zero`,
          display: true,
          type: "error",
        });
        return false;
      }

      if (
        config.monthlyInterestRate === null ||
        config.monthlyInterestRate === undefined ||
        config.monthlyInterestRate < 0
      ) {
        this.showToast({
          description: `${currency}: Monthly interest rate cannot be negative`,
          display: true,
          type: "error",
        });
        return false;
      }

      if (!config.maxLoanDuration || config.maxLoanDuration <= 0) {
        this.showToast({
          description: `${currency}: Max loan duration must be greater than zero`,
          display: true,
          type: "error",
        });
        return false;
      }

      if (
        config.loanCollateralRatio === null ||
        config.loanCollateralRatio === undefined ||
        config.loanCollateralRatio < 0
      ) {
        this.showToast({
          description: `${currency}: Loan collateral ratio cannot be negative`,
          display: true,
          type: "error",
        });
        return false;
      }

      if (
        config.gracePeriod === null ||
        config.gracePeriod === undefined ||
        config.gracePeriod < 0
      ) {
        this.showToast({
          description: `${currency}: Grace period cannot be negative`,
          display: true,
          type: "error",
        });
        return false;
      }

      return true;
    },
  },
};
</script>
  
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
.loan-configuration-container {
  width: 100%;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.overlay-container {
  position: relative;
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.6);
  z-index: 1;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  margin-bottom: 8px;
  font-size: 16px;
  color: #1a2139;
}

.form-group p {
  margin-bottom: 8px;
  font-size: 14px;
  color: #8791a7;
}

.form-group input {
  padding: 10px;
  border: 1px solid #e1e6f0;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
}

.form-group input::placeholder {
  font-size: 14px;
  color: #8791a7;
}

.form-group input:focus {
  outline: none;
  border-color: #4a90e2;
  box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
}

.checkbox-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.custom-checkbox {
  display: none;
}

.checkbox-group label {
  position: relative;
  cursor: pointer;
  padding-left: 28px;
  user-select: none;
  font-size: 14px;
  color: #333;
}

.checkbox-group .checkmark {
  position: absolute;
  top: 45%;
  left: 0;
  transform: translateY(-50%);
  height: 18px;
  width: 18px;
  background-color: #f0f0f0;
  border-radius: 2px;
  border: 1px solid #ccc;
}

.custom-checkbox:checked + label .checkmark {
  background-color: #007aff;
  border-color: #007aff;
  background-image: url('data:image/svg+xml;utf8,<svg width="10" height="7" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.99987 5.08625L8.59587 0.489746L9.30337 1.19675L3.99987 6.50025L0.817871 3.31825L1.52487 2.61125L3.99987 5.08625Z" fill="white"/></svg>');
  background-repeat: no-repeat;
  background-position: center;
  background-size: 10px 7px;
}

.button-container {
  margin-top: 20px;
}

.button-container button {
  width: fit-content;
  padding: 12px;
  background-color: #4d84ff;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s ease;
  display: flex;
  align-items: center;
  gap: 10px;
}

@media (max-width: 600px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}
</style>

<style lang="postcss" scoped>
::v-deep(.multiselect__tags) {
  @apply tw-pt-[0.65rem] tw-border-[#e1e6f0];
}

::v-deep(.multiselect__tag) {
  @apply tw-rounded-[50px] tw-bg-[#F0F5FF] tw-text-[#2F54EB] tw-px-[16px] tw-py-[8px] tw-flex;
}

::v-deep(.multiselect__tags-wrap) {
  @apply tw-flex;
}

::v-deep(.multiselect__placeholder) {
  @apply tw-text-[#8791A7] tw-text-base;
}

.multiselect__option--highlight {
  background: #e9f3ff;
  color: #0066ff;
}
</style>